'use client';

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck } from '@fortawesome/pro-solid-svg-icons';

interface ComparisonOption {
  id: string;
  label: string;
}

const COMPARISON_OPTIONS: ComparisonOption[] = [
  { id: 'month_over_month', label: 'Month over Month' },
  { id: 'quarter_over_quarter', label: 'Quarter over Quarter' },
  { id: 'year_over_year', label: 'Year over Year' },
  { id: 'other', label: 'Other' },
];

interface StrategyComparisonDropdownProps {
  selectedComparisonId?: string;
  onComparisonSelection: (comparison: ComparisonOption | null) => void;
}

const StrategyComparisonDropdown: React.FC<StrategyComparisonDropdownProps> = ({
  selectedComparisonId,
  onComparisonSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Filter comparison options based on search
  const filteredOptions = COMPARISON_OPTIONS.filter(
    (option: ComparisonOption) =>
      option.label.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Get selected comparison
  const selectedComparison = COMPARISON_OPTIONS.find(
    (option: ComparisonOption) => option.id === selectedComparisonId
  );

  const handleComparisonSelect = (option: ComparisonOption) => {
    onComparisonSelection(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const calculateDropdownPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const dropdownHeight = 250; // Approximate height of dropdown

    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  return (
    <div className="flex flex-col gap-1">
      <label className="text-primary text-sm font-semibold leading-tight">
        Comparison Period
      </label>

      {/* Dropdown Trigger */}
      <div className="relative">
        <button
          type="button"
          ref={triggerRef}
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-left flex justify-between items-center min-h-[48px]"
          data-testid="comparison-dropdown-trigger"
        >
          <div className="flex-1 flex items-center gap-2 overflow-hidden">
            {!selectedComparison ? (
              <span className="text-slate-500">
                Select comparison period...
              </span>
            ) : (
              <span className="text-slate-900">{selectedComparison.label}</span>
            )}
          </div>
          <Icon
            icon={faChevronDown}
            className={`w-4 h-4 text-slate-500 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className={`absolute left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-50 ${
              dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-slate-200">
              <input
                type="text"
                placeholder="Search comparison periods..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-sm"
                data-testid="comparison-search-input"
              />
            </div>

            {/* Options List */}
            <div className="max-h-40 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="p-3 text-sm text-slate-500">
                  No options found
                </div>
              ) : (
                <>
                  {/* Option to clear selection */}
                  {selectedComparisonId && (
                    <div
                      onClick={() => {
                        onComparisonSelection(null);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100"
                      data-testid="comparison-clear-option"
                    >
                      <span className="text-sm text-slate-500">
                        Clear selection
                      </span>
                    </div>
                  )}

                  {filteredOptions.map((option: ComparisonOption) => (
                    <div
                      key={option.id}
                      onClick={() => handleComparisonSelect(option)}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`comparison-option-${option.id}`}
                    >
                      <span className="text-sm text-slate-900">
                        {option.label}
                      </span>
                      {selectedComparisonId === option.id && (
                        <Icon
                          icon={faCheck}
                          className="w-4 h-4 text-sky-600 ml-auto"
                        />
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Click outside to close dropdown */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsOpen(false);
              setSearchTerm('');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default StrategyComparisonDropdown;
